import { usePlayer } from '../../stores/player';

/**
 * Shuffle and repeat, as the two small controls either side of the
 * transport.
 *
 * Off is faint and on is the accent; repeat-one adds a small 1. Both are
 * disabled while playing from a jam, where the order and the ending are
 * everybody's and not this player's to change.
 */
export function ShuffleControl({ size = 'sm' }: { size?: 'sm' | 'lg' }) {
  const shuffle = usePlayer((s) => s.shuffle);
  const toggleShuffle = usePlayer((s) => s.toggleShuffle);
  const jam = usePlayer((s) => s.source === 'jam');

  return (
    <ModeButton
      label={shuffle ? 'Shuffle off' : 'Shuffle'}
      active={shuffle}
      disabled={jam}
      onClick={toggleShuffle}
      size={size}
      data-shuffle={shuffle ? 'on' : 'off'}
    >
      <path d="M2 4h2.2c1 0 1.8.5 2.4 1.3L9.4 9.7c.6.8 1.4 1.3 2.4 1.3H13m0 0-1.7-1.7M13 11l-1.7 1.7M13 5h-1.2c-1 0-1.8.5-2.4 1.3l-.5.7M13 5l-1.7-1.7M13 5l-1.7 1.7M2 12h2.2c1 0 1.8-.5 2.4-1.3l.5-.7" />
    </ModeButton>
  );
}

export function RepeatControl({ size = 'sm' }: { size?: 'sm' | 'lg' }) {
  const repeat = usePlayer((s) => s.repeat);
  const cycleRepeat = usePlayer((s) => s.cycleRepeat);
  const jam = usePlayer((s) => s.source === 'jam');

  const label = repeat === 'off' ? 'Repeat' : repeat === 'all' ? 'Repeat one' : 'Repeat off';

  return (
    <ModeButton
      label={label}
      active={repeat !== 'off'}
      disabled={jam}
      onClick={cycleRepeat}
      size={size}
      data-repeat={repeat}
      badge={repeat === 'one' ? '1' : undefined}
    >
      <path d="M4 6.5V6a2 2 0 0 1 2-2h7m0 0-1.8-1.8M13 4l-1.8 1.8M12 9.5v.5a2 2 0 0 1-2 2H3m0 0 1.8 1.8M3 12l1.8-1.8" />
    </ModeButton>
  );
}

function ModeButton({
  label,
  active,
  disabled,
  onClick,
  size,
  badge,
  children,
  ...rest
}: {
  label: string;
  active: boolean;
  disabled: boolean;
  onClick: () => void;
  size: 'sm' | 'lg';
  badge?: string;
  children: React.ReactNode;
} & Record<`data-${string}`, string>) {
  const box = size === 'lg' ? 'h-12 w-12' : 'h-8 w-8';
  const icon = size === 'lg' ? 'h-5 w-5' : 'h-4 w-4';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      aria-pressed={active}
      title={disabled ? 'Not while listening in a jam' : label}
      className={`relative flex ${box} items-center justify-center rounded-full transition-colors disabled:opacity-30 ${
        active ? 'text-accent' : 'text-faint hover:bg-raised hover:text-ink'
      }`}
      {...rest}
    >
      <svg viewBox="0 0 16 16" className={icon} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {children}
      </svg>
      {badge !== undefined && (
        <span className="absolute -right-0.5 -top-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-accent px-0.5 font-mono text-[8px] font-bold leading-none text-accent-ink">
          {badge}
        </span>
      )}
      {active && badge === undefined && (
        <span aria-hidden className="absolute bottom-0.5 h-1 w-1 rounded-full bg-accent" />
      )}
    </button>
  );
}
